/**
 * DailyMatchEngagement — advisor-side chip showing how the buyer has
 * engaged with their Daily Matches feed (views + clicks + outbound taps).
 *
 * Silent when the lead has never engaged (or doesn't own Daily Matches).
 * Fetches on mount from /api/dealer/lead-engagement.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Eye, MousePointerClick, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DailyMatchEngagement = ({ email, compact = true }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    const target = (email || '').trim().toLowerCase();
    if (!target) return;
    let cancelled = false;
    axios.get(`${API}/dealer/lead-engagement`, { params: { lead_email: target } })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [email]);

  if (!data) return null;
  const { total_views = 0, total_clicks = 0, outbound_clicks = 0 } = data;
  if (total_views === 0 && total_clicks === 0) return null;  // silent when zero

  if (compact) {
    return (
      <div
        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold bg-cyan-500/15 text-cyan-300 border border-cyan-400/25"
        title={`Daily Matches engagement — ${total_views} views · ${total_clicks} clicks · ${outbound_clicks} outbound`}
        data-testid={`daily-engagement-chip-${(email || '').toLowerCase()}`}
      >
        <Eye className="w-3 h-3" /> {total_views}
        <span className="opacity-40">·</span>
        <MousePointerClick className="w-3 h-3" /> {total_clicks}
        {outbound_clicks > 0 && (
          <>
            <span className="opacity-40">·</span>
            <ExternalLink className="w-3 h-3" /> {outbound_clicks}
          </>
        )}
      </div>
    );
  }

  // Detailed expansion (used in the lead-detail modal / drawer)
  return (
    <div className="rounded-xl border border-cyan-400/25 bg-cyan-500/8 p-3" data-testid="daily-engagement-detail">
      <div className="flex items-center gap-2 mb-2">
        <p className="text-[10px] uppercase tracking-widest text-cyan-300 font-black">Daily Matches engagement</p>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-2">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-400">Views</p>
          <p className="text-lg font-black text-white">{total_views}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-400">Clicks</p>
          <p className="text-lg font-black text-white">{total_clicks}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-slate-400">Outbound</p>
          <p className="text-lg font-black text-emerald-300">{outbound_clicks}</p>
        </div>
      </div>
      {(data.per_match || []).slice(0, 4).map((row, i) => (
        <div key={i} className="flex items-center justify-between text-[11px] py-0.5">
          <span className="text-slate-300 truncate mr-2">{row.label}</span>
          <span className="text-slate-400 shrink-0">
            {row.views ? <><Eye className="inline w-3 h-3 mr-0.5" />{row.views}</> : null}
            {row.clicks ? <><MousePointerClick className="inline w-3 h-3 mx-1 ml-1.5" />{row.clicks}</> : null}
            {row.outbound && <ExternalLink className="inline w-3 h-3 text-emerald-300 ml-1" />}
          </span>
        </div>
      ))}
    </div>
  );
};

export default DailyMatchEngagement;
